import { useEffect, type CSSProperties, type ReactNode } from 'react';

// Standard modal widths for the whole app. 'standard' is the agreed system-wide
// width (1200px on a 1920-wide desktop); 'wide' is reserved for the rare
// data-grid modal and is NOT used yet — default is always 'standard'.
const SIZE_WIDTH: Record<'standard' | 'wide', number> = {
  standard: 1200,
  wide: 1480,
};

export interface AppModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  size?: 'standard' | 'wide';
}

/**
 * AppModal — the single standard modal shell for the app. Centered overlay,
 * fixed standard width, header + scrollable body + optional footer. Closes on
 * Escape, on overlay click, and via the header ✕ button. Uses the app's global
 * CSS classes/vars so it matches the existing prototype visual language.
 */
export function AppModal({
  title,
  subtitle,
  onClose,
  footer,
  children,
  size = 'standard',
}: AppModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 100,
  };
  const box: CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    overflow: 'hidden',
    width: SIZE_WIDTH[size],
    maxWidth: '95vw',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  };

  return (
    <div style={overlay} role="presentation" onClick={onClose}>
      <div
        style={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>{title}</h3>
            {subtitle && (
              <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn sm ghost"
            onClick={onClose}
            aria-label="ปิด"
            style={{ fontSize: 14, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>

        {footer && (
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid var(--border)',
              background: '#fafafa',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
